<template lang="pug">
app-main-content-layout(title='')
  .c-consents-page
    //- Header Section
    section.c-consents-page__header
      .c-consents-page__header-content
        h1.c-consents-page__heading Consents
        p.c-consents-page__subtitle Manage user consent records and privacy policies

    //- Warning if no organization selected
    section.c-consents-page__warning(v-if="!selectedOrg")
      el-alert(
        title="No Organization Selected"
        type="warning"
        description="Please select an organization from the dropdown to view consents."
        :closable="false"
        show-icon
      )
    
    template(v-else)
      //- Filters Section
      section.c-consents-filters-section
        ConsentFilters(
          v-model="searchQuery"
          @search="handleSearch"
        )

      //- Table Section
      section.c-consents-table-section
        //- Skeleton Loader
        template(v-if="loading")
          .c-skeleton-loader
            .c-skeleton-row(v-for="n in 10" :key="n")
              .c-skeleton-cell.c-skeleton-cell--user
              .c-skeleton-cell.c-skeleton-cell--version
              .c-skeleton-cell.c-skeleton-cell--basis
              .c-skeleton-cell.c-skeleton-cell--status
              .c-skeleton-cell.c-skeleton-cell--date
              .c-skeleton-cell.c-skeleton-cell--date
        
        //- Table
        el-table(
          v-else
          :data="filteredItems"
          style="width: 100%"
        )
          el-table-column(width="15")
          el-table-column(label="User" min-width="200")
            template(#default="scope")
              .lh-sm
                .font-black {{ scope.row.user.firstname }} {{ scope.row.user.lastname }}
          el-table-column(label="Policy Version" min-width="120")
            template(#default="scope")
              .lh-sm
                .font-medium {{ scope.row.policyVersion }}
          el-table-column(label="Basis" min-width="120")
            template(#default="scope")
              .lh-sm
                .font-medium {{ scope.row.basis }}
          el-table-column(label="Status" min-width="120")
            template(#default="scope")
              span.status-badge(:class="scope.row.isActive ? 'status-badge--active' : 'status-badge--inactive'") {{ scope.row.isActive ? 'Active' : 'Inactive' }}
          el-table-column(label="Created At" min-width="150")
            template(#default="scope")
              .lh-sm
                .font-medium {{ formatDate(scope.row.createdAt) }}
          el-table-column(label="Retention Expiry" min-width="150")
            template(#default="scope")
              .lh-sm
                .font-medium {{ formatDate(scope.row.retentionExpiry) }}
          
          el-table-column(width="15")
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, watch,
} from 'vue';
import { useStore } from 'vuex';
import globalShared from '@/shared';
import { organizationActiveConsents } from '@/graphs/gql';
import dayjs from 'dayjs';
import ConsentFilters from './ConsentFilters.vue';

interface User {
  id: string;
  firstname: string;
  lastname: string;
  avatar?: string;
}

interface Consent {
  id: string;
  policyVersion: string;
  basis: string;
  retentionExpiry: string;
  isActive: boolean;
  createdAt: string;
  user: User;
}

export default defineComponent({
  name: 'ManageConsents',
  components: { ConsentFilters },
  setup() {
    const store = useStore();
    const { makeGraphRequest, getAuthToken, ElMessage } = globalShared();
    
    const loading = ref(false);
    const items = ref<Consent[]>([]);
    const searchQuery = ref('');

    const selectedOrg = computed(() => {
      const org = store.getters['organizations/GET_SELECTED_ORGANIZATION'];
      return org;
    });

    const filteredItems = computed(() => {
      if (!searchQuery.value) {
        return items.value;
      }
      
      const query = searchQuery.value.toLowerCase();
      return items.value.filter((item) => {
        const fullName = `${item.user.firstname} ${item.user.lastname}`.toLowerCase();
        return fullName.includes(query);
      });
    });

    const fetchConsents = async () => {
      if (!selectedOrg.value?.id) {
        items.value = [];
        return;
      }

      const orgId = selectedOrg.value.id;
      
      loading.value = true;
      try {
        const response = await makeGraphRequest(
          'query',
          organizationActiveConsents,
          { orgId },
          getAuthToken(),
        );

        if (response?.errors) {
          console.error('[Consents] GraphQL errors:', response.errors);
          ElMessage.error(response.errors[0]?.message || 'Failed to fetch consents');
          return;
        }

        items.value = response?.data?.organizationActiveConsents || [];
      } catch (error) {
        console.error('[Consents] Error fetching consents:', error);
        ElMessage.error('Failed to fetch consents');
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (dateString: string) => {
      if (!dateString) return 'N/A';
      return dayjs(dateString).format('MM/DD/YYYY HH:mm');
    };

    const handleSearch = () => {
      // Search is handled by computed property
    };

    // Watch for organization changes
    watch(
      () => selectedOrg.value?.id,
      () => {
        fetchConsents();
      },
      { immediate: true },
    );

    return {
      loading,
      items,
      searchQuery,
      filteredItems,
      selectedOrg,
      formatDate,
      handleSearch,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables - Match Dashboard.vue and Activity page
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-consents-page {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 2rem;
    margin-bottom: 0;
  }

  &__header-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__heading {
    font-size: 1.875rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.5;
  }

  &__warning {
    margin-top: 0;
    margin-bottom: 0;
  }
}

// Filters Section (no card wrapper)
.c-consents-filters-section {
  margin-top: 0;
  margin-bottom: 0;
}

// Table Section - Match Activity page exactly
.c-consents-table-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;
  overflow: hidden;

  // Enable horizontal scrolling
  :deep(.el-table) {
    min-width: 100%;
    border-radius: 0;
  }

  :deep(.el-table__body-wrapper) {
    overflow-x: auto;
  }

  // Table styling - Match Activity page
  :deep(.el-table) {
    .cell {
      color: $gray-700;
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25rem;
      white-space: nowrap;
      padding: 0.5rem 0.5rem;
    }

    th > .cell {
      text-transform: capitalize;
      color: $gray-700;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      padding: 0.5rem 0.5rem;
    }

    th.el-table__cell.is-leaf {
      background-color: $gray-50;
      border-bottom: 1px solid $gray-100;
      padding: 0.5rem 0;
    }

    td.el-table__cell {
      border-bottom: 1px solid $gray-100;
      padding: 0.625rem 0;
    }

    .el-table__row {
      transition: background-color 0.15s ease;

      &:hover {
        background-color: $gray-50;
      }
    }

    .el-table__empty-block {
      min-height: 400px;
    }
  }
}

// Skeleton Loader
.c-skeleton-loader {
  padding: 0;
}

.c-skeleton-row {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid $gray-100;
  gap: 1rem;

  &:first-child {
    border-top: 1px solid $gray-100;
  }
}

.c-skeleton-cell {
  height: 20px;
  background: linear-gradient(
    90deg,
    $gray-100 0%,
    $gray-50 50%,
    $gray-100 100%
  );
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s ease-in-out infinite;
  border-radius: 4px;

  &--user {
    width: 200px;
    flex-shrink: 0;
  }

  &--version {
    width: 120px;
    flex-shrink: 0;
  }

  &--basis {
    width: 120px;
    flex-shrink: 0;
  }

  &--status {
    width: 100px;
    flex-shrink: 0;
  }

  &--date {
    width: 150px;
    flex-shrink: 0;
  }
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Status badges - Match Activity page badge style exactly
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
  border: 1px solid;

  &--active {
    background-color: #D1F4F0;
    color: #0D9488;
    border-color: #14B8A6;
  }

  &--inactive {
    background-color: $gray-100;
    color: $gray-600;
    border-color: $gray-300;
  }
}

// Font utilities
.font-black {
  font-weight: 600;
  color: $gray-900;
}

.font-medium {
  font-weight: 500;
  color: $gray-700;
}

.lh-sm {
  line-height: 1.25;
}

// Responsive Design
@media (max-width: 768px) {
  .c-consents-page {
    padding: 1.5rem;

    &__header {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }

    &__heading {
      font-size: 1.5rem;
    }

    &__subtitle {
      font-size: 0.8125rem;
    }
  }
}

@media (max-width: 480px) {
  .c-consents-page {
    padding: 1rem;

    &__heading {
      font-size: 1.25rem;
    }

    &__subtitle {
      font-size: 0.75rem;
    }
  }
}
</style>
